import { ValueOf } from '@ahha/utils/@types/typeUtils'
import { Paginated } from '@/api/Base/types'
import { ProjectPermissionsType } from '@/pages/Project/const'
import { DatasetNodeInfo } from '@/api/DatasetNode/types'
import { LabelingNodeInfo, ReviewNodeInfo } from '@/api/LabelingNode/types'
import { ModelNodeInfo } from '@/api/ModelNode/types'
import { ValidationNodeInfo } from '@/api/ValidationNode/types'
import { InferenceNodeInfo } from '@/api/InferenceNode/types'
import { ImageMatchingNodeInfo } from '@/api/ImageMatchingNode/types'
import { PreprocessingNodeInfo } from '@/api/PreprocessingNode/types'
import { PreprocessingResultNodeInfo } from '@/api/PreprocessingResultNode/types'
import { RoiNodeInfo } from '@/api/RoiNode/types'
import { GenericNode, NodeTypes } from '@/pages/RenewedPipeline/Nodes/types'

type CommonListPayload = {
  keyword?: string,
  page?: number,
  itemsPerPage?: number,
  sortKey?: string,
  sortDir?: string,
}

// TODO: 위치를 권한쪽으로 이동?
export type MemberType = {
  userId: string,
  role: string,
}

// FIXME: Edge 타입 파일 위치 어디다 둘지 고민 필요
export type Edge = {
  id: string,
  source: string,
  target: string,
  sourceHandle: string,
  targetHandle: string,
  type: 'customEdge',
  data: any,
  markerEnd: any,
}

// NOTE: 타입이름...?
export type Node = {
  nodeName: string,
  nodeType: ProjectNodeType,
  nodeId: string,
  sourceNodes: Node[] | null,
  branchNodes: Node[] | null,
  metadata: any,
  nodeInfo?: GenericNode
  position: { x: number, y: number }
  isHidden: boolean
  groupType?: string
}

export type Pipeline = {
  createdBy: string | null,
  updatedBy: string | null,
  createdAt: Date | null,
  updatedAt: Date | null,
  _id: string,
  projectId: string,
  nodes: Node[] | null,
  edges: Edge[] | null,
}

export const PIPELINE_TYPE = {
  BASIC: 'BASIC',
  OPERATIONS: 'OPERATIONS',
} as const
export type PipelineType = ValueOf<typeof PIPELINE_TYPE>

export interface ProjectDetail {
  createdBy: string,
  updatedBy: string,
  createdAt: string,
  updatedAt: string,
  _id: string,
  name: string,
  desc: string | null,
  owner: string,
  members: MemberType[],
  workedAt: string,
  lastWorkedUserId: string,
  thumbnailAccessibleUrl: string | null,
  datasetNodeList: DatasetNodeInfo[],
  preprocessingNodeList: PreprocessingNodeInfo[],
  labelingNodeList: LabelingNodeInfo[],
  reviewNodeList: ReviewNodeInfo[],
  imageMatchingNodeList: ImageMatchingNodeInfo[],
  modelNodeList: ModelNodeInfo[],
  validationNodeList: ValidationNodeInfo[],
  inferenceNodeList: InferenceNodeInfo[],
  preprocessingResultNodeList: PreprocessingResultNodeInfo[],
  roiNodeList: RoiNodeInfo[],
  pipeline?: Pipeline | null,
  opsPipeline?: Pipeline | null,
  hasUserUploadedThumbnail: boolean,
  workspaceId: string,
}

export type ProjectSummary = Omit<ProjectDetail, 'pipeline' | 'opsPipeline'>

export type ProjectType = {
  createdAt: string,
  createdBy: string,
  desc: string | null,
  lastWorkedUserId: string | null,
  members: MemberType[],
  name: string,
  owner: string,
  thumbnailAccessibleUrl: string | null,
  updatedAt: string | null,
  updatedBy: string | null,
  workedAt: string | null,
  _id: string,
}

export type GetProjectListPayload = Omit<CommonListPayload, 'keyword'> & {
  workspaceId?: string
  searchKeyword?: string
}
export type GetProjectListResponse = Paginated<ProjectType[]>

export type CreateProjectPayload = {
  workspaceId: string,
  name: string,
  desc?: string,
}

export type EditProjectPayload = Partial<CreateProjectPayload> & {
  projectId: string,
}

export type UpdateProjectThumbnailPayload = {
  projectId: string,
  file: File,
  isUserUploadedThumbnail?: boolean,
}

export type AddProjectMembersPayload = {
  projectId: string,
  permissionType: ProjectPermissionsType,
  userIds: string[],
}

export type UpdateMemberPermissionPayload = {
  projectId: string,
  userId: string,
  permissionType: ProjectPermissionsType,
}

export type UpdateMemberPermissionResponse = {
  user: string,
  updatedLabelingNodeIds: string[]
}

export type RemoveProjectMemberPayload = {
  projectId: string,
  userId: string,
}

export const PROJECT_NODE_TYPE = {
  DATASET: 'DATASET',
  LABELING: 'LABELING',
  REVIEW: 'REVIEW',
  MODEL_TRAINING: 'MODEL_TRAINING',
  MODEL_VALIDATION: 'MODEL_VALIDATION',
  MODEL_INFERENCE: 'MODEL_INFERENCE',
  IMAGE_MATCHING: 'IMAGE_MATCHING',
  ROI: 'ROI',
  PREPROCESSING: 'PREPROCESSING',
  PREPROCESSING_RESULT: 'PREPROCESSING_RESULT',
  DATA_DRIFT: 'DATA_DRIFT',
  MODEL_PERFORMANCE: 'MODEL_PERFORMANCE',
  MODEL_DRIFT: 'MODEL_DRIFT',
  MODEL_RETRAIN: 'MODEL_RETRAIN',
  MODEL_RETRAIN_EVALUATION: 'MODEL_RETRAIN_EVALUATION',
  MODEL_PIPELINE_SOURCE: 'MODEL_PIPELINE_SOURCE',
  MODEL_PIPELINE_INSTANCE: 'MODEL_PIPELINE_INSTANCE',
  MODEL_PIPELINE_GATE: 'MODEL_PIPELINE_GATE',
  MODEL_PIPELINE_MERGE: 'MODEL_PIPELINE_MERGE',
  MODEL_PIPELINE_RESULT: 'MODEL_PIPELINE_RESULT',
  GROUP: 'GROUP',
  DATA_STREAMING: 'DATA_STREAMING',
  EDA: 'EDA',
  EXTERNAL_MODEL: 'EXTERNAL_MODEL',
}
export type ProjectNodeType = ValueOf<typeof PROJECT_NODE_TYPE>

export const PROJECT_TYPE_MAP: Record<NodeTypes, ProjectNodeType> = {
  dataset: PROJECT_NODE_TYPE.DATASET,
  labeling: PROJECT_NODE_TYPE.LABELING,
  train: PROJECT_NODE_TYPE.MODEL_TRAINING,
  validation: PROJECT_NODE_TYPE.MODEL_VALIDATION,
  inference: PROJECT_NODE_TYPE.MODEL_INFERENCE,
  imageMatching: PROJECT_NODE_TYPE.IMAGE_MATCHING,
  roi: PROJECT_NODE_TYPE.ROI,
  preprocessing: PROJECT_NODE_TYPE.PREPROCESSING,
  preprocessingResult: PROJECT_NODE_TYPE.PREPROCESSING_RESULT,
  dataDrift: PROJECT_NODE_TYPE.DATA_DRIFT,
  modelDrift: PROJECT_NODE_TYPE.MODEL_DRIFT,
  modelPerformance: PROJECT_NODE_TYPE.MODEL_PERFORMANCE,
  modelRetrain: PROJECT_NODE_TYPE.MODEL_RETRAIN,
  modelRetrainEvaluation: PROJECT_NODE_TYPE.MODEL_RETRAIN_EVALUATION,
  modelPipelineSource: PROJECT_NODE_TYPE.MODEL_PIPELINE_SOURCE,
  modelPipelineInstance: PROJECT_NODE_TYPE.MODEL_PIPELINE_INSTANCE,
  modelPipelineGate: PROJECT_NODE_TYPE.MODEL_PIPELINE_GATE,
  modelPipelineMerge: PROJECT_NODE_TYPE.MODEL_PIPELINE_MERGE,
  modelPipelineResult: PROJECT_NODE_TYPE.MODEL_PIPELINE_RESULT,
  group: PROJECT_NODE_TYPE.GROUP,
  dataStreaming: PROJECT_NODE_TYPE.DATA_STREAMING,
  eda: PROJECT_NODE_TYPE.EDA,
  externalModel: PROJECT_NODE_TYPE.EXTERNAL_MODEL,
}

export type CopyPasteNodePayload = {
  projectId: string,
  nodeType: ProjectNodeType,
  nodeId: string,
  isCopyAll: boolean,
  isOpsNode: boolean,
  offsetX: number,
  offsetY: number,
  copiedNodeMetadata?: any
  dependentNodeMetadata?: any
  timestamp?: string // mmdd_hhmm
}

export type CopyPasteNodeResponse = {
  nodeType: ProjectNodeType,
  nodeId: string,
  result: GenericNode,
  nodes: Node[],
  edges: Edge[],
  dependentResult: GenericNode[] | null,
  dependentNodeType: ProjectNodeType | null,
}

export type UpdateNodePositionPayload = {
  projectId: string,
  nodes: {
    nodeId: string,
    nodeType: ProjectNodeType,
    position: { x: number, y: number },
  }[],
  isOpsNode?: boolean,
}

export type ConnectEdgePayload = {
  projectId: string,
  targetNodeId: string,
  targetNodeType: ProjectNodeType,
  sourceNodeType: ProjectNodeType,
  edge: Edge,
  isOpsNode?: boolean
}

export type DisconnectEdgePayload = {
  projectId: string,
  targetNodeId: string,
  targetNodeType: ProjectNodeType,
  sourceNodes: { nodeId: string, nodeType: ProjectNodeType }[],
  isOpsNode?: boolean,
}

export type DeleteNodePayload = {
  projectId: string,
  nodeId: string,
  isOpsNode: boolean,
}

export type UpdateNodeNamePayload = {
  projectId: string,
  name: string,
  nodeId: string,
  nodeType: ProjectNodeType,
}

export type ResizeGroupNodePayload = {
  projectId: string,
  groupNodeId: string,
  position: { x: number, y: number },
  width: number,
  height: number,
}
